'use client';

import { useTranslation } from 'next-i18next';
import { cn } from '@utils/cn';
import type { PromptBarMode } from './usePromptBarMode';
import { type ActionItem, usePromptBarActions } from './usePromptBarActions';

interface MissionChipsRowProps {
  locale: Locale;
  mode: PromptBarMode;
  isAuthenticated: boolean;
  className?: string;
}

/**
 * The mission list rendered as a centered row of chips below the prompt bar. The current
 * mission's chip looks identical to the rest but clicking it
 * does nothing (handleClick no-ops on the selected item). Wraps onto multiple rows on
 * narrow viewports.
 */
export const MissionChipsRow = ({ locale, mode, isAuthenticated, className }: MissionChipsRowProps) => {
  const { t } = useTranslation('common');
  const { candidateItems, companyItems, handleClick } = usePromptBarActions(locale, mode, isAuthenticated);
  const itemsByKey = new Map([...candidateItems, ...companyItems].map((item) => [item.key, item]));
  const chipItems = [
    { item: itemsByKey.get('jobs'), label: t('mc.chip.jobs') },
    { item: itemsByKey.get('coaching'), label: t('mc.chip.coach') },
    { item: itemsByKey.get('edit-profile'), label: t('mc.chip.profile') },
    { item: itemsByKey.get('add-job'), label: t('mc.chip.add') },
    { item: itemsByKey.get('candidate-management'), label: t('mc.chip.admin') },
  ].filter((entry): entry is { item: ActionItem; label: string } => Boolean(entry.item));

  return (
    <div className={cn('flex w-full flex-wrap items-center justify-center gap-1.5', className)}>
      {chipItems.map(({ item, label }) => (
        <button
          key={item.key}
          type="button"
          aria-current={item.selected ? 'page' : undefined}
          onClick={() => handleClick(item)}
          className={cn(
            'rounded-full border border-input bg-surface px-2.5 py-1 text-xs text-ink shadow-sm transition-colors lg:text-sm',
            item.selected ? 'font-semibold' : 'cursor-pointer hover:bg-surface-100',
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
};
